import { isEmpty } from 'lodash';
import { Queries } from '@shared/api';
import { getSharedApolloClient } from '@shared/api/client';
import { i18n } from '@shared/util/i18n';
import { MBLinkPreviewUtil } from '../util';

export function fetchLinkPreview(url: string) {
  return async (dispatch: MBDispatch, getState: MBGetState) => {
    const existingPreview = getState().chatLinkPreview.linkPreviewFromUrl[url];
    if (existingPreview) {
      return;
    }

    const linkPreview = await linkPreviewRequester.request(url);
    const defaultPreview: MBLinkPreviewData = {
      url,
      title: url,
      description: i18n('chat.linkPreview.defaultDescription'),
    };
    dispatch({
      type: 'CHAT:LINK_PREVIEW:UPDATE',
      value: { url, preview: linkPreview ?? defaultPreview },
    });
  };
}

class MBLinkPreviewDataRequester {
  private fetchRequests: Dict<Promise<MBLinkPreviewData>> = {};

  /*
        Store promises to fetch url data so we don't perform the same
        request more than once per link.
    */
  request(url: string): Promise<MBLinkPreviewData | null> {
    if (!!this.fetchRequests[url]) {
      return this.fetchRequests[url];
    }
    const promise = this.fetchFromAPI(url);
    this.fetchRequests[url] = promise;
    return promise;
  }

  /*
        TODO(fant): all of this logic should ideally be supplied by the backend.
        It's pretty bs that the client has to manually request all link details
        in separate requests...
    */
  private async fetchFromAPI(url: string): Promise<MBLinkPreviewData | null> {
    interface FetchLinkDetailsQueryResponse {
      data: {
        details: {
          provider_url: string;
          description: string;
          title: string;
          url: string;
          version: string;
          provider_name: string;
          type: 'link';
          thumbnail_width?: number;
          thumbnail_height?: number;
          thumbnail_url?: string;
        };
      };
    }

    const apolloClient = getSharedApolloClient();
    const res: FetchLinkDetailsQueryResponse = await apolloClient.query<
      MBAPI.FetchLinkDetailsQuery,
      MBAPI.FetchLinkDetailsQueryVariables
    >({
      query: Queries.link.details,
      variables: { url },
      // Note(fant): need to specify fetch policy when querying
      // using apollo client directly (network vs cache)
      fetchPolicy: 'network-only',
    });

    // res.data.details is {} (empty object) for invalid urls
    // If we get an url that fails, we try to request details
    // from the page's main url (ex github.com instead of
    // github.com/townsquared). In this way, we can create
    // some previews even though we can't get it for the
    // specific url.
    if (!res?.data?.details || isEmpty(res?.data?.details)) {
      const baseUrl = MBLinkPreviewUtil.baseUrlFromUrl(url);
      if (!baseUrl || url === baseUrl) {
        return null;
      }
      return this.fetchFromAPI(baseUrl);
    }

    return {
      // We return the url from the API, which will eg have prefixed
      // "https://" if it's missing (eg for lol.com => https://lol.com)
      // If this returns null (e.g. for Google Drive urls - 503?),
      // we return the provided url. The provided url will fail if
      url: url.indexOf('http') !== 0 ? `https://${url}` : url,
      title: res.data.details.title,
      description: res.data.details.description,
      imageUrl: res.data.details.thumbnail_url,
      imageWidth: res.data.details.thumbnail_width,
      imageHeight: res.data.details.thumbnail_height,
    };
  }
}

export const linkPreviewRequester = new MBLinkPreviewDataRequester();
